import { useTranslation } from "@redotech/redo-i18n";
import { RedoRadioGroup } from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import {
  type AccountServiceLevelSelection,
  type MerchantCarrierAccountsChoice,
  type ReturnRateSourceOption,
} from "../rate-source-selection-utils";
import { getAllSpecificNoneRadioOptions } from "./all-specific-none-radio-options";
import { RateSourceSelectionSectionSkeleton } from "./rate-source-selection-section-skeleton";
import { SpecificMerchantCarrierAccountServiceLevelAccordion } from "./specific-merchant-carrier-account-service-level-accordion";
import { SpecificRateSourceCheckbox } from "./specific-rate-source-checkbox";

export interface MerchantCarrierAccountsSectionProps {
  loading: boolean;
  choice: MerchantCarrierAccountsChoice;
  options: ReturnRateSourceOption[];
  selectedOptionIds: Set<ReturnRateSourceOption["id"]>;
  serviceLevelSelections: Map<
    ReturnRateSourceOption["id"],
    AccountServiceLevelSelection
  >;
  onChoiceChange(choice: MerchantCarrierAccountsChoice): void;
  onOptionChange(option: ReturnRateSourceOption, selected: boolean): void;
  onAllServiceLevelsChange(
    option: ReturnRateSourceOption,
    selected: boolean,
  ): void;
  onServiceLevelToggle(
    option: ReturnRateSourceOption,
    serviceLevel: string,
    selected: boolean,
  ): void;
}

type SpecificMerchantCarrierAccountOptionsProps = Omit<
  MerchantCarrierAccountsSectionProps,
  "loading" | "choice" | "onChoiceChange"
>;

export function MerchantCarrierAccountsSection({
  loading,
  choice,
  options,
  selectedOptionIds,
  serviceLevelSelections,
  onChoiceChange,
  onOptionChange,
  onAllServiceLevelsChange,
  onServiceLevelToggle,
}: MerchantCarrierAccountsSectionProps) {
  const { t } = useTranslation();

  return (
    <Flex dir="column" gap="sm">
      <Text fontSize="xs" textColor="secondary">
        {t("Your carrier accounts")}
      </Text>
      {loading ? (
        <RateSourceSelectionSectionSkeleton
          rowWidths={["40px", "112px", "48px"]}
          showSectionTitle={false}
        />
      ) : (
        <RedoRadioGroup<MerchantCarrierAccountsChoice>
          gap="sm"
          options={getAllSpecificNoneRadioOptions({
            allLabel: t("All"),
            noneLabel: t("None"),
            specificLabel: t("Specific accounts"),
            specificChildren: (
              <SpecificMerchantCarrierAccountOptions
                onAllServiceLevelsChange={onAllServiceLevelsChange}
                onOptionChange={onOptionChange}
                onServiceLevelToggle={onServiceLevelToggle}
                options={options}
                selectedOptionIds={selectedOptionIds}
                serviceLevelSelections={serviceLevelSelections}
              />
            ),
          })}
          setValue={onChoiceChange}
          size="xs"
          value={choice}
        />
      )}
    </Flex>
  );
}

function SpecificMerchantCarrierAccountOptions({
  options,
  selectedOptionIds,
  serviceLevelSelections,
  onOptionChange,
  onAllServiceLevelsChange,
  onServiceLevelToggle,
}: SpecificMerchantCarrierAccountOptionsProps) {
  return (
    <Flex dir="column" gap="sm" pl="xl">
      {options.map((option) =>
        option.serviceLevelOptions?.length ? (
          <SpecificMerchantCarrierAccountServiceLevelAccordion
            key={option.id}
            onAllServiceLevelsChange={(selected) => {
              onAllServiceLevelsChange(option, selected);
            }}
            onServiceLevelToggle={(serviceLevel, selected) => {
              onServiceLevelToggle(option, serviceLevel, selected);
            }}
            option={option}
            selection={
              serviceLevelSelections.get(option.id) ?? { kind: "none" }
            }
          />
        ) : (
          <SpecificRateSourceCheckbox
            key={option.id}
            onChange={(selected) => {
              onOptionChange(option, selected);
            }}
            option={option}
            selected={selectedOptionIds.has(option.id)}
          />
        ),
      )}
    </Flex>
  );
}
